import React, { FC, useState, useEffect } from 'react';

import { Alert, Box, Button, CardContent, Grid, TextField, Typography } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

import { BackflowJobItem } from '../../model';

import axios from '../../../sharedModules/utils/axios.utils';

interface AuthorityProvidedFieldsProps {
  backflowJobItem: BackflowJobItem;
}

interface AuthorityProvidedValue {
  id?: string;
  value?: string;
  field_label?: string;
}

const AuthorityProvidedFields: FC<AuthorityProvidedFieldsProps> = (props) => {
  const { backflowJobItem } = props;

  const [authorityProvidedValues, setAuthorityProvidedValues] = useState<AuthorityProvidedValue[]>([]);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [originalValues, setOriginalValues] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const fetchAuthorityProvidedValues = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get(`/backflows/authority_provided_values`, {
        params: {
          backflow_job_item_id: backflowJobItem.id,
        },
      });

      const values = response.data.result;
      setAuthorityProvidedValues(values);

      const initialFormValues: Record<string, string> = {};
      values.forEach((valueRecord: AuthorityProvidedValue) => {
        if (valueRecord.id) {
          initialFormValues[valueRecord.id] = valueRecord.value || '';
        }
      });
      setFormValues(initialFormValues);
      setOriginalValues(initialFormValues);

      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Something went wrong fetching the Authority-Provided Values');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const updatePromises = authorityProvidedValues.map((valueRecord) => {
        if (valueRecord.id) {
          return axios.put(`/backflows/authority_provided_values/${valueRecord.id}`, {
            value: formValues[valueRecord.id] || '',
          });
        }
        return Promise.resolve();
      });

      await Promise.all(updatePromises);
      setSuccessMessage('Authority-Provided Fields updated successfully');
      // setOriginalValues({ ...formValues });
      await fetchAuthorityProvidedValues();
    } catch (error) {
      setErrorMessage('Something went wrong updating the Authority-Provided Fields');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFieldChange = (id: string, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const hasChanges = (): boolean => {
    return Object.keys(formValues).some((id) => {
      return formValues[id] !== (originalValues[id] || '');
    });
  };

  useEffect(() => {
    if (backflowJobItem.id) {
      fetchAuthorityProvidedValues();
    }
  }, [backflowJobItem.id]);

  return (
    <Grid>
      <CardContent>
        <LoadingWrapper isLoading={loading} message="Loading">
          <ErrorMessage message={errorMessage} />
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}
          <Box display="flex" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="600">
              Authority-Provided Fields
            </Typography>
          </Box>

          {authorityProvidedValues.length === 0 ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No authority-provided fields configured for this water authority.
              </Typography>
            </Box>
          ) : (
            <form onSubmit={handleFormSubmit}>
              <Box sx={{ mb: 3 }}>
                {authorityProvidedValues.map((valueRecord) => {
                  return (
                    <Box key={valueRecord.id} sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        label={valueRecord.field_label}
                        value={formValues[valueRecord.id || ''] || ''}
                        onChange={(e) => handleFieldChange(valueRecord.id || '', e.target.value)}
                        variant="outlined"
                        size="small"
                        disabled={submitting}
                      />
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={submitting || !hasChanges()}
                >
                  {submitting ? 'Saving...' : 'Save All Fields'}
                </Button>
              </Box>
            </form>
          )}
        </LoadingWrapper>
      </CardContent>
    </Grid>
  );
};

export default AuthorityProvidedFields;
